import { assign, createMachine } from 'xstate';
import { Application, AppStatuses, ChannelTypes, Dealer, Details, FormTypes, PayPeriodTypes } from '../gql/graphql';
import { acceptedDomain, conditionallySetupSocureSession, conditionallySetupTMXSession } from '../helpers';
import { NETWORK_ERROR, sharedActions } from './actions/genericActions';
import CustomerLookupMachine from './customer-lookup-machine';
import EntryPointMachine from './entrypoint-machine';
import ExistingApplicationMachine from './existing-application-machine';
import longFormApplicationMachine from './long-form-application-machine';
import NewApplicationMachine from './new-application-machine';
import OtbMachine from './otb-machine';
import { CustomerLookupMachineContext, CustomerLookupStates, STARTING_PAGE_OTP_BYPASS } from './types/customerLookupMachine.types';
import {
  EcommMachineEvents,
  EcommMachineMachineContext,
  EcommMachineMachineEvents,
  EcommMachineMachineStates,
  EcommMachineStates,
  ValidatedByTokenType,
} from './types/ecommMachine.types';
import VirtualCardMachine from './virtual-card-machine';
import { isLongForm } from '../helpers/converters/isLongForm';
import { canSkipItemEntry, getEcommApprovalOnly } from '../helpers/leases/leases.helpers';
import { LongFormMachineContext, LongFormStates } from './types/longFormMachine.types';
import { identify, page, track } from './actions/segment';
import { koalafiRoles, merchantRoles, Role } from '../types/role';
import { initializeNeuroIdFromContext, shouldProcessNeuroId } from '../helpers/neuroId/neuroId.helper';
import { ExistingApplicationMachineContext } from './types/existingApplicationMachine.types';
import { NewApplicationMachineContext } from './types/newApplicationMachine.types';
import { OtbMachineContext } from './types/otbMachine.types';
import { LDClient as LDClientType } from 'launchdarkly-js-client-sdk';
import { transferToItemManagement } from '../helpers/cookies/unified.helper';
import { canAccessContractSummaryPage, canAccessMerchantContractSummaryPage } from '../helpers/contractSummaryAccess/contractSummaryAccess';
import { EntrypointValues, QueryParamKeys } from '../constants/query-params';
import { resolveAndPersistMetadataParams } from '../helpers/params/metadataParams';
import verifyHybridToken from '../networkRequests/security/verifyHybridToken';
import verifyOobaToken, { TokenErrors } from '../networkRequests/security/verifyOobaToken';
import verifyContractToken from '../networkRequests/security/verifyContractToken';
import createOrder from '../networkRequests/order/createOrder';
import queryOrderPreAuth from '../networkRequests/order/queryOrderPreAuth';
import { buildAnalytics } from '../helpers/analytics/buildAnalytics';
import { getQueryParam } from '../helpers/params/params';
import queryHighestSupportedApplicationMilestone from '../networkRequests/featureFlags/getApplicationMilestone';
import getApplicationItems from '../networkRequests/cart/getApplicationItems';
import { buildLDMultiContext } from '../helpers/launchDarkly/ldContext.helper';
import { initializeLDClientWithRetry, LD_INIT_FAILED } from '../helpers/launchDarkly/ldInit.helper';
import { applyInitialLanguage } from '../helpers/language/language.helper';
import { AppConfig } from '../app.config';
import { sessionId } from '../apollo';
import { getDealerCookie } from '../helpers/cookies/cookie.helper';
import { setAppStatusParams } from '../helpers/url/url.helpers';
import { launchPageTypes } from '../types/launchPageTypes';
import { FLAG_NEUROID_ENABLED, FLAG_SPANISH_ENABLED } from '../constants/feature-flags';
import { determinePlaidFF } from '../helpers/plaid/plaid.helper';

const ecommMachine = () => {
  return createMachine<EcommMachineMachineContext, EcommMachineMachineEvents, EcommMachineMachineStates>(
    {
      id: 'ECommerceMachine',
      initial: EcommMachineStates.EntryPointV2,
      context: {
        orderId: '',
        dealer: {} as Dealer,
        application: undefined,
        orderDetails: {} as Details,

        highestSupportedApplicationMilestone: 3,
        launchPage: new URLSearchParams(document.location.search.substring(1)).get(QueryParamKeys.LaunchPage) as launchPageTypes | null,
        reviewPage: new URLSearchParams(document.location.search.substring(1)).has(QueryParamKeys.ReviewPage),
        contractSummaryPageParam: new URLSearchParams(document.location.search.substring(1)).has(QueryParamKeys.ContractSummaryPage),
        merchantContractSummaryPageParam: new URLSearchParams(document.location.search.substring(1)).has(QueryParamKeys.MerchantContractSummaryPage),

        role: Role.Customer,
        shouldProcessNeuroId: false,
        socureDeviceSessionId: null,

        entryPointPage: 'OTPVerification',

        cellPhone: '',
        didSkipOTP: false,
        networkError: null,
        skipApproval: false,
        bankDetails: { accountNumber: '', routingNumber: '', bankName: '' }, // TODO figure out a way to make this nullable
        cart: [],

        validatedByTokenType: undefined,
        applicationItems: [],

        ldClient: {} as LDClientType,
        // TODO: once we can actually get app items, don't hardcode this, obviously
        hasAppItems: true,
        entrypointParam: '',
        entrypointSessionId: undefined,
      },
      on: {
        [NETWORK_ERROR]: [{ actions: [assign({ networkError: (_context, event) => event?.data })] }],
        [EcommMachineEvents.TRACK]: {
          actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
        },
        [EcommMachineEvents.PAGE]: {
          actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
        },
        [EcommMachineEvents.IDENTIFY]: {
          actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
        },
      },
      states: {
        [EcommMachineStates.EntryPointV2]: {
          id: 'EntryPointV2',
          initial: 'Phase1_QueryParams',
          states: {
            Phase1_QueryParams: {
              /**
               * App Initialization, Phase 1:
               *  - Read metadata params, strip from URL and store in local storage
               */
              entry: [
                assign({ entrypointParam: () => resolveAndPersistMetadataParams().entrypoint ?? '' }),
                assign({ entrypointSessionId: () => getQueryParam(QueryParamKeys.EntrypointSessionId) ?? undefined }),
                assign({ orderId: () => getQueryParam(QueryParamKeys.OrderId) || '' }),
                assign({ role: () => getDealerCookie() ?? Role.Customer }),
                (_ctx, _event) => {
                  if (document.fonts) {
                    document.fonts.forEach((fontFace) => {
                      if (fontFace.status !== 'loaded' && fontFace.status !== 'loading') {
                        fontFace.load();
                      }
                    });
                  }
                },
                (ctx, _event) => {
                  const baseProps = {
                    sourceWindowPath: window.location.pathname,
                    UI_redesign: 'UI_redesign',
                    smRefactor: 'smRefactor',
                  };
                  window.analytics?.track(`User role: ${ctx.role}`, baseProps);
                  window.analytics?.track('Set user session id', { sessionId, ...baseProps });

                  //check for both the referrer param we are deprecating and the new param for now
                  //TODO: remove the check on the referrer param after enough time has passed and we don't
                  //anticipate too much more traffic with this param in the url
                  if (ctx.entrypointParam === EntrypointValues.VirtualCardEmail || getQueryParam(QueryParamKeys.Referrer) === 'vce') {
                    window.analytics?.track('Return To Virtual Card Email CTA Clicked', baseProps);
                  }
                },
              ],
              always: { target: 'Phase2_IndependentCalls' },
            },
            Phase2_IndependentCalls: {
              /**
               * App Initialization, Phase 2:
               *  In parallel, resolve/validate (if applicable/available):
               *    - Hybrid Token
               *    - Ooba Token
               *    - Contract Token
               */
              type: 'parallel',
              onDone: { target: 'Phase3_OrderResolution' },
              states: {
                HybridToken: {
                  initial: 'pending',
                  states: {
                    pending: {
                      always: [{ cond: () => !!getQueryParam(QueryParamKeys.HybridToken), target: 'verifying' }, { target: 'done' }],
                    },
                    verifying: {
                      tags: 'calling',
                      entry: (_ctx, _event) => window.analytics?.track('Attempting Hybrid Verification'),
                      invoke: {
                        id: 'verifyHybridToken',
                        src: () => verifyHybridToken({ hybridToken: getQueryParam(QueryParamKeys.HybridToken)! }),
                        onDone: {
                          target: 'done',
                          actions: [
                            assign({ validatedByTokenType: (_ctx, _event) => ValidatedByTokenType.HYBRID }),
                            (_ctx, event) => window.analytics?.track(event.data === true ? 'Hybrid Verification Successful' : 'Hybrid Verification Failed'),
                          ],
                        },
                        onError: { target: 'done' },
                      },
                    },
                    done: { type: 'final' },
                  },
                },
                OobaToken: {
                  initial: 'pending',
                  states: {
                    pending: {
                      always: [{ cond: () => !!getQueryParam(QueryParamKeys.OobaToken), target: 'verifying' }, { target: 'done' }],
                    },
                    verifying: {
                      tags: 'calling',
                      entry: (_ctx, _event) => window.analytics?.track('Attempting Ooba Verification'),
                      invoke: {
                        id: 'verifyOobaToken',
                        src: () => verifyOobaToken({ oobaToken: getQueryParam(QueryParamKeys.OobaToken)! }),
                        onDone: [
                          {
                            cond: (_ctx, event) => event.data[0] === true,
                            target: 'done',
                            actions: assign({ validatedByTokenType: (_ctx, _event) => ValidatedByTokenType.OOBA }),
                          },
                          {
                            // An expired OOBA token means the emailed link is no longer usable.
                            // Send the customer to the dedicated "This link is expired" page (regardless of app status).
                            cond: (_ctx, event) => event.data[1] === TokenErrors.Expired,
                            actions: (_ctx, _event) => window.analytics?.track('Ooba Verification Failed', { reason: 'expired' }),
                            target: '#EntryPointV2.ExpiredLink',
                          },
                          {
                            // A bad/invalid link (or an AES/verification error) is not the same as an expired one —
                            // show the generic InitError page rather than telling the customer their link expired.
                            actions: assign({
                              networkError: (_ctx, _event) => ({
                                resolverErrors: [],
                                forbiddenError: { displayText: 'Internal error' },
                              }),
                            }),
                            target: '#EntryPointV2.InitError',
                          },
                        ],
                        onError: {
                          target: '#EntryPointV2.InitError',
                          actions: assign({
                            networkError: (_ctx, _event) => ({
                              resolverErrors: [],
                              forbiddenError: { displayText: 'Internal error' },
                            }),
                          }),
                        },
                      },
                    },
                    done: { type: 'final' },
                  },
                },
                ContractToken: {
                  initial: 'pending',
                  states: {
                    pending: {
                      always: [{ cond: () => !!getQueryParam(QueryParamKeys.ContractToken), target: 'verifying' }, { target: 'done' }],
                    },
                    verifying: {
                      tags: 'calling',
                      entry: (_ctx, _event) => window.analytics?.track('Attempting Contract Token Verification'),
                      invoke: {
                        id: 'verifyContractToken',
                        src: () => verifyContractToken({ contractToken: getQueryParam(QueryParamKeys.ContractToken)! }),
                        onDone: [
                          {
                            cond: (_ctx, event) => event.data[0] === true,
                            target: 'done',
                            actions: assign({ validatedByTokenType: (_ctx, _event) => ValidatedByTokenType.CONTRACT }),
                          },
                          {
                            target: '#EntryPointV2.InitError',
                            actions: assign({
                              networkError: (_ctx, event) => ({
                                resolverErrors: [],
                                unauthorizedError: event.data[1] === TokenErrors.Expired ? { displayText: 'Session time out' } : undefined,
                                forbiddenError: event.data[1] === TokenErrors.Expired ? undefined : { displayText: 'Internal error' },
                              }),
                            }),
                          },
                        ],
                        onError: {
                          target: '#EntryPointV2.InitError',
                          actions: assign({
                            networkError: (_ctx, _event) => ({
                              resolverErrors: [],
                              forbiddenError: { displayText: 'Internal error' },
                            }),
                          }),
                        },
                      },
                    },
                    done: { type: 'final' },
                  },
                },
              },
            },
            Phase3_OrderResolution: {
              /**
               * App Initialiation, Phase 3:
               *  If there is an orderId in context, query it.
               *  Otherwise, create a new order.
               */
              initial: 'CheckOrderId',
              onDone: { target: 'Phase4_OrderDependentCalls' },
              states: {
                CheckOrderId: {
                  always: [
                    { cond: (ctx) => !!ctx.orderId, target: 'callingQueryOrderPreAuth' },
                    // If no dealerId, show an error page rather than a blank page
                    { target: 'callingCreateOrder' },
                  ],
                },
                callingCreateOrder: {
                  tags: 'calling',
                  invoke: {
                    id: 'createOrder',
                    src: () => createOrder({ analytics: buildAnalytics() }),
                    onDone: {
                      target: 'callingQueryOrderPreAuth',
                      actions: [
                        assign({ orderId: (_ctx, event) => event.data?.data?.createOrder?.order?.id ?? '' }),
                        (ctx, _event) => {
                          if (ctx.orderId) {
                            const url = new URL(document.location.href);
                            url.searchParams.set(QueryParamKeys.OrderId, ctx.orderId);
                            window.history.pushState({}, '', url);
                          }
                        },
                      ],
                    },
                    onError: {
                      target: '#EntryPointV2.InitError',
                      actions: assign({ networkError: (_ctx, event) => event.data }),
                    },
                  },
                },
                callingQueryOrderPreAuth: {
                  tags: 'calling',
                  invoke: {
                    id: 'queryOrderPreAuth',
                    src: (ctx) => queryOrderPreAuth(ctx.orderId),
                    onDone: {
                      target: 'done',
                      actions: [
                        assign({
                          orderDetails: (_ctx, event) => event.data?.details as Details,
                          dealer: (_ctx, event) => event.data?.dealer as Dealer,
                          application: (_ctx, event) => event.data?.application || undefined,
                          performBAV: (_ctx, event) => event.data?.metadata?.performBAV,
                        }),
                        (_ctx, event) => {
                          const publicDealerId = event.data?.dealer?.publicDealerId;
                          if (publicDealerId) sessionStorage.setItem('publicDealerId', publicDealerId);
                        },
                        (_ctx, event) => {
                          const customer = event.data?.details?.customer;
                          const dealer = event.data?.dealer;
                          window.dtrum?.sendSessionProperties(undefined, undefined, {
                            firstname: customer?.firstName as string,
                            lastname: customer?.lastName as string,
                            emailaddress: customer?.emailAddress as string,
                            publicdealerid: dealer?.publicDealerId as string,
                            storename: dealer?.storeName as string,
                            orderid: event.data?.id as string,
                          });
                          if (customer?.emailAddress) {
                            window.dtrum?.identifyUser(customer.emailAddress as string);
                          }
                        },
                        (_ctx, event) => setAppStatusParams(event.data?.application?.lease?.status),
                        (ctx, _event) => conditionallySetupTMXSession(ctx.orderId, ctx.role),
                        (_ctx, event) => {
                          const customerId = event.data?.application?.customer?.id;
                          if (customerId) identify(customerId);
                        },
                      ],
                    },
                    onError: {
                      target: '#EntryPointV2.InitError',
                      actions: assign({ networkError: (_ctx, event) => event.data }),
                    },
                  },
                },
                done: { type: 'final' },
              },
            },
            Phase4_OrderDependentCalls: {
              /**
               * App Initialiation, Phase 4:
               *  Calls we needed order info in order to make. In parallel:
               *    - Init LaunchDarkly, then depending on flags:
               *      - Resolve language
               *      - Init NeuroId
               *    - Query the HighestSupportedApplicationMilestone
               *      - We should probably just pull this from LaunchDarkly, since the flag lives there now, and save the network call
               *    - Set up the Socure device session
               */
              type: 'parallel',
              onDone: { target: 'Phase5_ApplicationItems' },
              states: {
                LaunchDarkly: {
                  initial: 'initializing',
                  states: {
                    initializing: {
                      tags: 'calling',
                      invoke: {
                        id: 'initializeLDClient',
                        src: (ctx) => {
                          const context = buildLDMultiContext(
                            {
                              storeDealerId: ctx.dealer.id,
                              companyDealerId: ctx.dealer.companyDealerId,
                            },
                            ctx.application?.customer?.id
                              ? {
                                  custSorId: ctx.application.customer.id,
                                  applicationId: ctx.application?.lease?.id || ctx.application?.loan?.id,
                                  applicationNumber: ctx.application?.lease?.displayId || ctx.application?.loan?.displayId || undefined,
                                }
                              : undefined,
                          );
                          return initializeLDClientWithRetry(AppConfig.launchDarklyApiKey, context, { streaming: true });
                        },
                        onDone: {
                          target: 'resolvingLanguage',
                          actions: assign({ ldClient: (_ctx, event) => event.data }),
                        },
                        onError: {
                          target: '#EntryPointV2.InitError',
                          actions: assign({
                            networkError: (_ctx, event) => ({
                              resolverErrors: [],
                              errorGeneral: { displayText: 'Failed to initialize LaunchDarkly', developerText: LD_INIT_FAILED, stackTrace: event.data },
                            }),
                          }),
                        },
                      },
                    },
                    resolvingLanguage: {
                      entry: (ctx) => applyInitialLanguage(ctx.ldClient?.variation?.(FLAG_SPANISH_ENABLED, false) ?? false),
                      always: { target: 'initializingNeuroId' },
                    },
                    // Runs after LD init because the NeuroId enable flag lives in LD.
                    initializingNeuroId: {
                      entry: [
                        assign({
                          shouldProcessNeuroId: (ctx) =>
                            // NeuroId should generally be on for all stores, so it will default to true
                            shouldProcessNeuroId(ctx.dealer, ctx.role, ctx.ldClient?.variation?.(FLAG_NEUROID_ENABLED, true) ?? true),
                        }),
                        initializeNeuroIdFromContext,
                      ],
                      always: { target: 'done' },
                    },
                    done: { type: 'final' },
                  },
                },
                Milestone: {
                  initial: 'CheckChannel',
                  states: {
                    // Ecomm hardcodes milestone to 3, so skip the network call for ecomm dealers.
                    // Tom and I are unsure why it's hardcoded to 3 (lost commit history in sm-refactor), but incrementing
                    // this to 4 would likely cause issues.
                    // Exception: merchant-managed ("tele-sales") ecomm apps need the real milestone to reach the
                    // B&M contract summary, and post-signing re-entry needs it for the view-only lease agreement.
                    //
                    // Virtual card ecomm dealers are out of scope for tele-sales and keep the hardcoded
                    // milestone regardless of params or tokens.
                    CheckChannel: {
                      always: [
                        {
                          cond: (ctx) =>
                            ctx.dealer?.channelType === ChannelTypes.Ecommerce &&
                            (!!ctx.dealer?.isVirtualCard ||
                              (!ctx.contractSummaryPageParam &&
                                !ctx.merchantContractSummaryPageParam &&
                                ctx.validatedByTokenType !== ValidatedByTokenType.OOBA &&
                                ctx.validatedByTokenType !== ValidatedByTokenType.CONTRACT)),
                          target: 'done',
                          actions: assign({ highestSupportedApplicationMilestone: (_ctx, _event) => 3 }),
                        },
                        { target: 'fetching' },
                      ],
                    },
                    fetching: {
                      tags: 'calling',
                      invoke: {
                        id: 'queryHighestSupportedApplicationMilestone',
                        src: (ctx) => queryHighestSupportedApplicationMilestone(ctx.dealer.id),
                        onDone: {
                          target: 'done',
                          actions: assign({ highestSupportedApplicationMilestone: (_ctx, event) => event.data }),
                        },
                        onError: {
                          target: '#EntryPointV2.InitError',
                          actions: assign({ networkError: (_ctx, event) => event.data }),
                        },
                      },
                    },
                    done: { type: 'final' },
                  },
                },
                Socure: {
                  initial: 'initializing',
                  states: {
                    initializing: {
                      invoke: {
                        id: 'setupSocureSession',
                        src: (ctx) => conditionallySetupSocureSession(ctx.role),
                        onDone: {
                          target: 'done',
                          actions: [
                            assign({ socureDeviceSessionId: (_ctx, event) => event.data }),
                            (_ctx, event) => {
                              window.analytics?.track('Socure Device Session', { captured: !!event.data });
                            },
                          ],
                        },
                        // A Socure setup failure must not block application initialization.
                        onError: {
                          target: 'done',
                          actions: () => {
                            window.analytics?.track('Socure Device Session', { captured: false });
                          },
                        },
                      },
                    },
                    done: { type: 'final' },
                  },
                },
              },
            },
            Phase5_ApplicationItems: {
              /**
               * App Initialiation, Phase 5:
               *  - Get Application Items, if applicable
               */
              initial: 'CheckShouldFetch',
              onDone: { target: `#EcommMachine.${EcommMachineStates.Loading}` },
              states: {
                CheckShouldFetch: {
                  always: [
                    {
                      cond: (ctx) => {
                        const hasContractSummaryParam = ctx.contractSummaryPageParam || ctx.merchantContractSummaryPageParam;
                        const isMilestone4 = ctx.highestSupportedApplicationMilestone > 3;
                        const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(ctx.role));
                        const hasLeaseAppId = !!ctx.application?.lease?.id;
                        const isLeaseAppApproved = ctx.application?.lease?.status === AppStatuses.Approved;
                        return hasContractSummaryParam && isMilestone4 && hasElevatedRole && hasLeaseAppId && isLeaseAppApproved;
                      },
                      target: 'fetching',
                    },
                    { target: 'done' },
                  ],
                },
                fetching: {
                  tags: 'calling',
                  invoke: {
                    id: 'getApplicationItems',
                    src: (ctx) => getApplicationItems(ctx),
                    onDone: {
                      target: 'done',
                      actions: assign({ applicationItems: (_ctx, event) => event.data ?? [] }),
                    },
                    onError: {
                      target: '#EntryPointV2.InitError',
                      actions: assign({ networkError: (_ctx, event) => event.data }),
                    },
                  },
                },
                done: { type: 'final' },
              },
            },
            InitError: {},
            /**
             * Terminal state for an expired or invalid OOBA link — the customer is shown the
             * "This link is expired" page. We intentionally don't resolve the dealer here: token
             * verification runs before authentication, so an order/dealer lookup would be
             * unauthenticated (401). The page uses generic copy without a merchant name.
             */
            ExpiredLink: {},
          },
        },
        [EcommMachineStates.EcommMachine]: {
          id: 'EcommMachine',
          initial: EcommMachineStates[EcommMachineStates.Loading],
          states: {
            [EcommMachineStates.Loading]: {
              exit: [
                //   // This corresponds to the cb in the invoke that calls DetermineOrderPath. Not needed currently
                // assign({
                //   orderId: (_, { orderId }) => orderId,
                //   dealer: (_, { dealer }) => dealer,
                //   orderDetails: (_, { orderDetails }) => orderDetails,
                //   application: (_, { application }) => application,
                //   highestSupportedApplicationMilestone: (_, { highestSupportedApplicationMilestone }) => highestSupportedApplicationMilestone,
                // }),
                'postLoadingDone',
              ],
              tags: 'Loading',
              on: {
                [EcommMachineEvents.APP_IN_PROGRESS]: [
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                      ctx.application?.lease?.status === AppStatuses.Approved &&
                      (ctx.role === Role.Customer || ctx.role === Role.Admin) &&
                      !!ctx.hasAppItems &&
                      ctx.highestSupportedApplicationMilestone >= 4 &&
                      isLongForm(ctx.dealer),
                    actions: assign({ longFormApplicationStartingPage: 'ContractSummaryBM' }),
                  },
                  {
                    target: '#ECommerceMachine.ExistingApplication',
                    cond: (ctx) =>
                      ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                      ctx.application?.lease?.status === AppStatuses.Approved &&
                      (ctx.role === Role.Customer || ctx.role === Role.Admin) &&
                      !!ctx.hasAppItems &&
                      ctx.highestSupportedApplicationMilestone >= 4 &&
                      ctx.dealer.formType == 'short',
                    actions: assign({ existingApplicationStartingPage: 'ContractSummaryBM' }),
                  },
                  {
                    cond: (ctx) => {
                      return (
                        ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                        ctx.application?.lease?.status === AppStatuses.Approved &&
                        merchantRoles.includes(ctx.role) &&
                        !!ctx.hasAppItems &&
                        ctx.highestSupportedApplicationMilestone >= 4
                      );
                    },
                    actions: (ctx) => {
                      // if the OOBA token validated successfully, lease will be present
                      transferToItemManagement(ctx.application?.lease!);
                    },
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                      ctx.application?.lease?.status === AppStatuses.Approved &&
                      !ctx.hasAppItems &&
                      ctx.highestSupportedApplicationMilestone >= 4,
                    actions: assign({ longFormApplicationStartingPage: 'Approval' }),
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                      ctx.application?.lease?.status === AppStatuses.DocsReady &&
                      ctx.highestSupportedApplicationMilestone >= 4,
                    actions: assign({ longFormApplicationStartingPage: 'LeaseAgreement' }),
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      ctx.validatedByTokenType === ValidatedByTokenType.OOBA &&
                      ctx.application?.lease?.status === AppStatuses.DocsSigned &&
                      ctx.highestSupportedApplicationMilestone >= 4,
                    actions: assign({ longFormApplicationStartingPage: 'LeaseComplete' }),
                  },
                  {
                    target: '#ECommerceMachine.OTB',
                    cond: 'isDraftOtbApplication',
                    actions: assign({ otbInitialState: 'OTB_APPLY' }),
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      (canAccessContractSummaryPage(ctx) || canAccessMerchantContractSummaryPage(ctx)) &&
                      (ctx.dealer?.formType == FormTypes.Long || ctx.dealer?.formType == FormTypes.LongV2),
                    // no skip to page, use status checks to navigate
                  },
                  {
                    target: '#ECommerceMachine.ExistingApplication',
                    cond: (ctx) => canAccessContractSummaryPage(ctx) || canAccessMerchantContractSummaryPage(ctx),
                    // no skip to page, use status checks to navigate
                  },
                  {
                    cond: (context) => canSkipItemEntry(context.dealer, !!context.application?.lease?.contractSummaries, context.entrypointParam),
                    target: '#ECommerceMachine.ExistingApplication',
                    actions: assign({ existingApplicationStartingPage: 'ApprovalWithSkipItemEntry' }),
                  },
                  {
                    target: '#ECommerceMachine.OTB',
                    cond: (ctx) => !!ctx.application?.lease?.isOtb, // Will only have the app at this point if they already have a hybrid token
                    actions: assign({ otbInitialState: 'APPROVED' }),
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: (ctx) =>
                      (ctx.dealer?.formType === FormTypes.Long || ctx.dealer?.formType === FormTypes.LongV2) &&
                      (ctx.application?.lease?.status === AppStatuses.Approved || ctx.application?.lease?.status === AppStatuses.DocsReady),
                  },
                  // OR-2278: removed the M2 EntryPointDraftAppReviewAndSubmit -> transferToRapid case here;
                  // all stores are M3+ now, and Rapid/Loans transfer targets no longer exist.
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: 'LongFormDraftApp',
                  },
                  {
                    target: '#ECommerceMachine.LongFormApplication',
                    cond: 'EntryPointDraftAppReviewOnly',
                  },
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'EntryPointApprovalConfirmation',
                    actions: assign({ entryPointPage: 'ApprovalConfirmation' }),
                  },
                  {
                    target: '#ECommerceMachine.ExistingApplication',
                    cond: (ctx) =>
                      ctx.dealer.channelType === ChannelTypes.BrickAndMortar &&
                      ctx.highestSupportedApplicationMilestone > 3 &&
                      ctx.dealer.formType === FormTypes.Short,
                    // catch all for in progress, short form M4 brick and mortar
                  },
                  {
                    cond: 'BetweenApprovalAndContractSummary',
                    target: '#ECommerceMachine.ExistingApplication', // This is target state where entrypoint machine is used less
                    actions: assign({ entryPointPage: 'Approval' }),
                  },
                  // This absolutely has to be above the remaining targets as it is handling the cart validation.
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'EcommLFHasCartValidationErr',
                    actions: assign({ entryPointPage: 'PreApproval' }),
                  },
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'EntryPointContractSummary',
                    actions: assign({ entryPointPage: 'ContractSummary' }),
                  },
                  // This is temporary while plaid is still a ff.  Once A/B testing is done, will keep 1 guard
                  {
                    target: '#ECommerceMachine.ExistingApplication',
                    cond: 'LeaseBankAccountErrorFF',
                    actions: assign({ existingApplicationStartingPage: 'BankDetailsFF' }),
                  },
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'LeaseBankAccountError',
                    actions: assign({ entryPointPage: 'BankAccountError' }),
                  },
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'LeaseDebitCardError',
                    actions: assign({ entryPointPage: 'DebitCardError' }),
                  },
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    cond: 'LeasePreApproved',
                    actions: assign({ entryPointPage: 'PreApproval' }),
                  },
                  {
                    target: '#ECommerceMachine.CustomerLookupFlow',
                    cond: 'ShortFormDraftApp',
                    actions: assign({ customerLookupStartingPage: 'BasicInfo' }),
                  },
                  {
                    target: '#ECommerceMachine.ExistingApplication', // Uses target state approach, which is to not use the entrypoint
                    actions: assign({ existingApplicationStartingPage: 'Prequalification' }),
                  },
                  // There really needs to be a default statement here
                ],
                [EcommMachineEvents.ERROR]: [{ actions: 'postLoadingDone' }],
                [EcommMachineEvents.APP_FINISHED]: [
                  {
                    target: '#ECommerceMachine.EntryPoint',
                    actions: assign({ entryPointPage: 'Done' }),
                  },
                ],
                [EcommMachineEvents.CUSTOMER_LOOKUP_EVENT]: {
                  target: '#ECommerceMachine.EntryPoint',
                  actions: assign({ entryPointPage: 'OTPRequest' }),
                },
                [EcommMachineEvents.DECLINE]: {
                  target: '#ECommerceMachine.EntryPoint',
                  actions: assign({ entryPointPage: 'LeaseDecline' }),
                },
              },
              invoke: {
                src: (ctx) => async (cb) => {
                  try {
                    // dealer is required for DetermineOrderPath, but application is not
                    if (!!ctx.dealer) {
                      // Leaving this here because without redux, this is where I'd want to fetch the order
                      // const response = await fetchOrder(orderId);
                      const getEventType = DetermineOrderPath(ctx.dealer, ctx.application, ctx.reviewPage, canAccessContractSummaryPage(ctx));
                      // Again, leaving this here. Not necessary since we already have the order
                      // However, when we fetch the order from the machine will need to return this
                      // Put this back in if/when we move queryOrder/createOrder into the state machine
                      cb({
                        type: getEventType,
                        // dealer,
                        // application,
                        // highestSupportedApplicationMilestone,
                      });
                    } else {
                      throw Error('User Data is null');
                    }
                  } catch (e) {
                    console.log(e);
                    // cb({ type: UpdateEvents.ERROR });
                  }
                },
              },
            },
          },
        },
        [EcommMachineStates.EntryPoint]: {
          invoke: {
            id: 'EntryPointMachine',
            src: EntryPointMachine,
            data: {
              page: (ctx: EcommMachineMachineContext) => ctx.entryPointPage,
              orderId: (ctx: EcommMachineMachineContext) => ctx.orderId,
              orderDetails: (ctx: EcommMachineMachineContext) => ctx.orderDetails,
              application: (ctx: EcommMachineMachineContext) => ctx.application,
              dealer: (ctx: EcommMachineMachineContext) => ctx.dealer,
              highestSupportedApplicationMilestone: (ctx: EcommMachineMachineContext) => ctx.highestSupportedApplicationMilestone,
              customerFlowType: (ctx: EcommMachineMachineContext) => ctx.customerFlowType,
              applicationItems: (ctx: EcommMachineMachineContext) => ctx.applicationItems,
              role: (ctx: EcommMachineMachineContext) => ctx.role,
              // This machine maps context explicitly (no ...ctx spread), so entrypoint props must be
              // forwarded here or analytics events from the entrypoint flow drop them (e.g. OTP Request).
              entrypointParam: (ctx: EcommMachineMachineContext) => ctx.entrypointParam,
              entrypointSessionId: (ctx: EcommMachineMachineContext) => ctx.entrypointSessionId,
            },
          },
          on: {
            [EcommMachineEvents.CUSTOMER_LOOKUP_EVENT]: {
              target: EcommMachineStates.CustomerLookupFlow,
              actions: assign({ customerLookupStartingPage: 'OTPCheck' }),
            },
            [EcommMachineEvents.SKIP_OTP_EVENT]: [
              {
                target: EcommMachineStates.CustomerLookupFlow,
                actions: assign((_, event) => ({
                  ...event.data,
                  customerLookupStartingPage: STARTING_PAGE_OTP_BYPASS,
                })),
              },
            ],
            [EcommMachineEvents.EXISTING_APPLICATION]: {
              target: EcommMachineStates.ExistingApplication,
              actions: assign((_, event) => ({
                ...event.data,
                // TODO: put this in the entrypoint machine
                existingApplicationStartingPage: (() => {
                  switch (event?.data?.source) {
                    case 'ContractSummary.NEXT':
                      return 'LeaseAgreement';
                    case 'ContractSummary.BACK':
                      return 'CartSummary';
                    case 'BankAccountError.NEXT':
                      return 'BankDetails';
                    case 'DebitCardError.NEXT':
                      return 'DebitCardEntry';
                    case 'ContractSummary.EDIT_DEBIT_INFO': // why was this deleted?
                      return 'DebitCardEntry';
                    default:
                      return undefined;
                  }
                })(),
              })),
            },
            [EcommMachineEvents.LONG_FORM]: {
              target: EcommMachineStates.LongFormApplication,
              actions: assign((context, event) => ({
                ...event.data,
                // TODO: put this in the entrypoint machine
                longFormApplicationStartingPage: (() => {
                  switch (event?.data?.source) {
                    case 'Approved':
                      return 'ApprovalConfirmation';
                    case 'ContractSummary':
                      return 'ContractSummary';
                    case 'Declined':
                      return 'Declined';
                    case 'Error':
                      return 'ReviewAndSubmit';
                    case 'CompleteAppLater':
                      return 'CompleteAppLater';
                    case LongFormStates.LeaseAgreement:
                      return LongFormStates.LeaseAgreement;
                  }
                  if (context.reviewPage) {
                    return 'ReviewAndSubmit';
                  }
                })(),
              })),
            },
            [EcommMachineEvents.PHONE_SUBMITTED]: {
              target: EcommMachineStates.CustomerLookupFlow,
              actions: assign({
                customerLookupStartingPage: (_context, _event) => CustomerLookupStates.OTPCheck,
                cellPhone: (_context, event) => event.data.cellPhone,
              }),
            },
          },
        },
        [EcommMachineStates.ExistingApplication]: {
          invoke: {
            id: 'ExistingApplicationMachine',
            src: ExistingApplicationMachine,
            data: (ctx: EcommMachineMachineContext): ExistingApplicationMachineContext => ({
              // TODO: clean this up
              fromContractSummary: false,
              canGoBackViaHeader: false,
              updateBankAccountErr: undefined,
              plaidToken: {
                linkToken: '',
                publicToken: '',
              },
              bankConnection: {
                plaidLoading: false,
                paymentSelectionError: undefined,
                isTokenizedAcct: false,
                bankAcctLast4: '',
              },
              updateEmploymentErrors: [],
              generateContractErrors: [],
              signingErrors: [],
              ipAddress: '',
              leaseSigned: false,
              loanSigned: false,
              cardExpiration: 0,
              embedUrl: '',
              embedUrlError: null,
              removeVirtualCardErrors: [],
              itemToEdit: null,
              updateDebitInfoErrors: [],
              selectLoanOptionResponse: null,
              loanDocs: null,

              ...ctx,

              // these are below the ctx to ensure overwrites
              basicInfo: ctx.basicInfo || {
                firstName: ctx?.application?.customer?.firstName ?? '',
                lastName: ctx?.application?.customer?.lastName ?? '',
                emailAddress: ctx?.application?.customer?.emailAddress ?? '',
              },
              basicInfoContinued: ctx.basicInfoContinued || {
                birthDate: ctx?.application?.customer?.birthDate ?? '',
                taxpayerIdentificationNumber: '', // raw SSN would never be returned by order query
                tokenizedTaxId: ctx?.application?.customer?.tokenizedTaxId ?? '',
                monthlyIncome: ctx?.application?.lease?.employment?.monthlyIncome ?? '',
              },
              cellPhone: (ctx.cellPhone || ctx?.application?.customer?.cellPhone) ?? '',
              accommodationAddress: ctx.accommodationAddress || {
                line1: ctx?.application?.customer?.accommodationAddress?.line1 ?? '',
                line2: ctx?.application?.customer?.accommodationAddress?.line2 ?? undefined,
                city: ctx?.application?.customer?.accommodationAddress?.city ?? '',
                state: ctx?.application?.customer?.accommodationAddress?.state ?? '',
                zip: ctx?.application?.customer?.accommodationAddress?.zip ?? '',
              },
              billingAddress: ctx.billingAddress || {
                line1: ctx?.application?.customer?.billingAddress?.line1 ?? '',
                line2: ctx?.application?.customer?.billingAddress?.line2 ?? undefined,
                city: ctx?.application?.customer?.billingAddress?.city ?? '',
                state: ctx?.application?.customer?.billingAddress?.state ?? '',
                zip: ctx?.application?.customer?.billingAddress?.zip ?? '',
              },
              payFrequencyDetails: ctx.payFrequency || {
                paymentFrequency: ctx?.application?.lease?.employment?.payPeriod ?? ('' as PayPeriodTypes),
                lastPayDate: ctx?.application?.lease?.employment?.lastPayDate ?? '',
                nextPayDate: ctx?.application?.lease?.employment?.nextPayDate ?? '',
                isPaidViaDirectDeposit: ctx?.application?.lease?.employment?.isPaidViaDirectDeposit ?? false,
              },

              bankDetails: ctx.bankDetails || {
                accountNumber: '',
                routingNumber: ctx?.application?.lease?.bank?.routingNumber ?? '',
              },
              // TODO: this feels like a problem for refreshes on the R&S screen
              debitInfo: {
                cardNumber: '',
                cardholderFirstName: '',
                cardholderLastName: '',
                expirationDate: '',
              },

              skipToPage: ctx.existingApplicationStartingPage,
              applicationItems: ctx.applicationItems,
              getExistingLeaseDocumentsErrors: [],
              updateOrderErrors: [],
              cartTotal: 0,
            }),
          },
          on: {
            [EcommMachineEvents.VIRTUAL_CARD_APP]: {
              target: '#ECommerceMachine.VirtualCard',
            },
          },
        },
        [EcommMachineStates.CustomerLookupFlow]: {
          invoke: {
            id: 'CustomerLookupMachine',
            src: CustomerLookupMachine,
            data: (ctx: EcommMachineMachineContext): CustomerLookupMachineContext => ({
              isLast4Different: false,
              didCustomerChange: false,

              //  TODO: these need their types adjusted
              sendCodeErrors: [],
              checkCodeErrors: [],
              resolverErrors: [],

              ...ctx,

              skipToPage: ctx.customerLookupStartingPage,
            }),
          },
          on: {
            [EcommMachineEvents.NEW_APPLICATION]: {
              actions: assign((context, event) => ({ ...event.data })),
              target: '#ECommerceMachine.NewApplication',
            },
            [EcommMachineEvents.OTB_CUSTOMER]: {
              actions: assign((context, event) => ({ ...event.data })),
              target: '#ECommerceMachine.OTB',
            },
            [EcommMachineEvents.LONG_FORM]: {
              actions: assign((context, event) => ({ ...event.data })),
              target: EcommMachineStates.LongFormApplication,
            },
            [EcommMachineEvents.EXISTING_APPLICATION]: [
              {
                cond: 'VirtualCardDealer',
                target: '#ECommerceMachine.ExistingApplication',
                actions: assign((context, event) => ({ ...event.data })),
              },
              {
                cond: 'LeaseDebitCardError',
                actions: assign((context, event) => ({ ...event.data })),
                target: '#ECommerceMachine.ExistingApplication',
              },
              {
                cond: 'IsLongForm',
                target: '#ECommerceMachine.LongFormApplication',
                actions: assign((context, event) => ({
                  ...event.data,
                  existingApplicationStartingPage: event?.data?.fromCustomerLookup ? 'Preapproval' : '',
                })),
              },
              {
                target: '#ECommerceMachine.ExistingApplication',
                actions: assign((context, event) => ({
                  ...event.data,
                  existingApplicationStartingPage: event?.data?.fromCustomerLookup ? 'Preapproval' : '',
                })),
              },
            ],
          },
        },
        [EcommMachineStates.VirtualCard]: {
          invoke: {
            id: 'VirtualCardMachine',
            src: VirtualCardMachine,
            data: {
              order: (ctx: EcommMachineMachineContext) => ({
                id: ctx.orderId,
                dealer: ctx.dealer,
                application: ctx.application,
                details: ctx.orderDetails,
              }),
              highestSupportedApplicationMilestone: (ctx: EcommMachineMachineContext) => ctx.highestSupportedApplicationMilestone,
            },
          },
        },
        [EcommMachineStates.OTB]: {
          entry: 'log',
          invoke: {
            id: 'OtbMachine',
            src: OtbMachine,
            data: (ctx: EcommMachineMachineContext): OtbMachineContext => ({
              isLast4Different: false,

              ...ctx,

              initialState: ctx.otbInitialState,
            }),
          },
          on: {
            [EcommMachineEvents.VIRTUAL_CARD_APP]: {
              target: '#ECommerceMachine.VirtualCard',
            },
            [EcommMachineEvents.EXISTING_APPLICATION]: [
              {
                target: '#ECommerceMachine.LongFormApplication',
                cond: 'IsLongForm',
                actions: assign((_, event) => ({ ...event.data })),
              },
              {
                target: '#ECommerceMachine.ExistingApplication',
                actions: assign((_, event) => ({ ...event.data })),
              },
            ],
            [EcommMachineEvents.NEW_APPLICATION]: [
              { target: '#ECommerceMachine.LongFormApplication', cond: 'IsLongForm', actions: assign((context, event) => ({ ...event.data })) },
              {
                target: '#ECommerceMachine.CustomerLookupFlow',
                actions: assign((context, event) => ({ ...event.data, customerLookupStartingPage: 'BasicInfo' })),
              },
            ],
          },
        },
        [EcommMachineStates.NewApplication]: {
          invoke: {
            id: 'NewApplicationMachine',
            src: NewApplicationMachine,
            data: (ctx: EcommMachineMachineContext, event): NewApplicationMachineContext => ({
              ...ctx,
            }),
          },
          on: {
            [EcommMachineEvents.BACK]: {
              target: EcommMachineStates.CustomerLookupFlow,
              actions: assign({
                customerLookupStartingPage: (_context, _event) => 'BasicInfoContinued',
              }),
            },
            [EcommMachineEvents.EXISTING_APPLICATION]: {
              target: '#ECommerceMachine.ExistingApplication',
              actions: assign((context, event) => ({ ...event.data })),
            },
            [EcommMachineEvents.VIRTUAL_CARD_APP]: {
              target: '#ECommerceMachine.VirtualCard',
            },
            [EcommMachineEvents.DECLINE]: {
              target: '#ECommerceMachine.Declined',
            },
          },
        },
        [EcommMachineStates.Declined]: {
          always: [
            {
              target: '#ECommerceMachine.LeaseDeclined',
            },
          ],
        },
        [EcommMachineStates.LoanDeclined]: {},
        [EcommMachineStates.LeaseDeclined]: {},
        [EcommMachineStates.LongFormApplication]: {
          invoke: {
            id: 'LongFormApplicationMachine',
            src: longFormApplicationMachine,
            data: (ctx: EcommMachineMachineContext): LongFormMachineContext => ({
              // these are above the ctx to provide defaults
              additionalCustomerInfo: undefined,
              plaidToken: {
                linkToken: '',
                publicToken: '',
              },
              bankConnection: {
                plaidLoading: false,
                isTokenizedAcct: false,
                bankAcctLast4: '',
              },
              didCustomerChange: false,
              generateContractErrors: [],
              signingErrors: [],
              leaseSigned: false,
              loanSigned: false,
              dismissibleAlerts: [],
              ...ctx,

              // these are below the ctx to ensure overwrites
              basicInfo: ctx.basicInfo || {
                firstName: ctx?.application?.customer?.firstName ?? '',
                lastName: ctx?.application?.customer?.lastName ?? '',
                emailAddress: ctx?.application?.customer?.emailAddress ?? '',
              },
              basicInfoContinued: ctx.basicInfoContinued || {
                birthDate: ctx?.application?.customer?.birthDate ?? '',
                taxpayerIdentificationNumber: '', // raw SSN would never be returned by order query
                tokenizedTaxId: ctx?.application?.customer?.tokenizedTaxId ?? '',
                monthlyIncome: ctx?.application?.lease?.employment?.monthlyIncome ?? '',
              },
              cellPhone: (ctx.cellPhone || ctx?.application?.customer?.cellPhone) ?? '',
              accommodationAddress: ctx.accommodationAddress || {
                line1: ctx?.application?.customer?.accommodationAddress?.line1 ?? '',
                line2: ctx?.application?.customer?.accommodationAddress?.line2 ?? undefined,
                city: ctx?.application?.customer?.accommodationAddress?.city ?? '',
                state: ctx?.application?.customer?.accommodationAddress?.state ?? '',
                zip: ctx?.application?.customer?.accommodationAddress?.zip ?? '',
              },
              billingAddress: ctx.billingAddress || {
                line1: ctx?.application?.customer?.billingAddress?.line1 ?? '',
                line2: ctx?.application?.customer?.billingAddress?.line2 ?? undefined,
                city: ctx?.application?.customer?.billingAddress?.city ?? '',
                state: ctx?.application?.customer?.billingAddress?.state ?? '',
                zip: ctx?.application?.customer?.billingAddress?.zip ?? '',
              },
              payFrequencyDetails: ctx.payFrequency || {
                paymentFrequency: ctx?.application?.lease?.employment?.payPeriod ?? ('' as PayPeriodTypes),
                lastPayDate: ctx?.application?.lease?.employment?.lastPayDate ?? '',
                nextPayDate: ctx?.application?.lease?.employment?.nextPayDate ?? '',
                isPaidViaDirectDeposit: ctx?.application?.lease?.employment?.isPaidViaDirectDeposit ?? false,
              },
              bankDetails: {
                accountNumber: '', // populated by getAdditionalCustomerInfo
                routingNumber: ctx?.application?.lease?.bank?.routingNumber ?? '',
              },
              debitInfo: {
                cardNumber: '', // populated by getAdditionalCustomerInfo
                cardholderFirstName: ctx?.application?.customer?.firstName ?? '',
                cardholderLastName: ctx?.application?.customer?.lastName ?? '',
                expirationDate: ctx.application?.lease?.card?.expirationDate ?? '',
              },

              skipToPage: ctx.longFormApplicationStartingPage,
              isReadOnly: ctx.reviewPage,
              getExistingLeaseDocumentsErrors: [],
            }),
          },
          on: {
            [EcommMachineEvents.OTB_CUSTOMER]: {
              target: '#ECommerceMachine.OTB',
              actions: assign((context, event) => ({ ...event.data })),
            },
            [EcommMachineEvents.CUSTOMER_LOOKUP_EVENT]: {
              target: '#ECommerceMachine.CustomerLookupFlow',
              actions: assign((context, event) => ({
                ...event.data,
                cellPhone: '',
                customerLookupStartingPage: null,
              })),
            },
          },
        },
      },
      schema: {
        context: {} as EcommMachineMachineContext,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
    },
    {
      actions: {
        postLoadingDone: (context, _event) => {
          window.parent.postMessage(JSON.stringify({ stopSpinner: true }), acceptedDomain(context.dealer?.domains!)!);
        },
        ...sharedActions,
      },
      guards: {
        IsLongForm: (context, event) => context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2,
        LongFormDraftApp: (context) =>
          (context.application?.lease?.status === 'draft' || context.application?.lease?.status === 'errorsFound') &&
          (context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2),
        ShortFormDraftApp: (context) => context.application?.lease?.status === 'draft' && context.dealer?.formType === FormTypes.Short,
        VirtualCardDealer: (context, _event) => !!context.dealer?.isVirtualCard,
        LeaseBankAccountError: (context, event) =>
          event?.data?.application?.lease?.declineReason === 'bankAccount' || context.application?.lease?.declineReason === 'bankAccount',
        LeaseBankAccountErrorFF: (context, event) =>
          (event?.data?.application?.lease?.declineReason === 'bankAccount' || context.application?.lease?.declineReason === 'bankAccount') &&
          context.dealer?.formType !== FormTypes.Long &&
          context.dealer?.formType !== FormTypes.LongV2 &&
          determinePlaidFF(context),
        LeaseDebitCardError: (context, event) =>
          event?.data?.application?.lease?.declineReason === 'debitCardError' || context.application?.lease?.declineReason === 'debitCardError',
        LeasePreApproved: (context, event) => context.application?.lease?.status === 'preApproved' && context.dealer?.formType !== FormTypes.Short,
        EntryPointApprovalConfirmation: (context, event) =>
          (context.application?.lease?.status === 'approved' || context.application?.lease?.status === 'docsReady') &&
          (getEcommApprovalOnly(context.dealer) ||
            (context.dealer?.channelType === ChannelTypes.BrickAndMortar && context.highestSupportedApplicationMilestone < 4)),
        BetweenApprovalAndContractSummary: (context, event) => {
          const isApproved = context.application?.lease?.status === 'approved';
          const isVC = context.dealer?.isVirtualCard;
          const missingItems = !context.orderDetails?.items?.length;
          const missingContractSummary = !context.application?.lease?.contractSummaries?.length;
          return isApproved && isVC && (missingItems || missingContractSummary);
        },
        EntryPointContractSummary: (context, event) => {
          // Should not skip if OTB (unless OTB + VC)
          if ((!!context.application?.lease?.isOtb && !context.dealer?.isVirtualCard) || getEcommApprovalOnly(context.dealer)) {
            return false;
          } else {
            return context.application?.lease?.status === 'docsReady' || context.application?.lease?.status === 'approved';
          }
        },
        EntryPointDraftAppReviewOnly: (context, event) => context.reviewPage,
        isDraftOtbApplication: (context, _event) => {
          return context.application?.lease?.status === 'draft' && !!context.application?.lease?.isOtb;
        },
        EcommLFHasCartValidationErr: (context, event) => {
          // check if ecomm long form and if theres a cart validation issue
          const isELF =
            context.dealer.channelType === ChannelTypes.Ecommerce &&
            !context.dealer.isVirtualCard &&
            (context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2);

          const hasCartValidationError =
            !!context.application?.lease?.cartValidationDetails || // deprecated
            !!context.application?.lease?.cartValidationAdditionalDetails?.cartValidationType ||
            context.orderDetails.items?.length === 0;
          return isELF && hasCartValidationError;
        },
      },
    },
  );
};

const DetermineOrderPath = (dealer: Dealer, application: Application | undefined, reviewPage: boolean, contractSummaryPage: boolean): EcommMachineEvents => {
  const leaseAppId = application?.lease?.id;
  const leaseAppStatus = application?.lease?.status;
  const loanAppId = application?.loan?.id;
  const loanAppStatus = application?.loan?.status;
  const declineReason = application?.lease?.declineReason;
  const isVirtualCard = dealer?.isVirtualCard;
  const isShortForm = dealer?.formType === FormTypes.Short;

  if (reviewPage || contractSummaryPage) {
    return EcommMachineEvents.APP_IN_PROGRESS;
  }

  if (leaseAppId && leaseAppStatus) {
    // Need to use OTB, VirtualCard, ExistingApplication or New Application....
    switch (leaseAppStatus) {
      case 'readyForFunding':
      case 'approvedForFunding':
      case 'funded':
      case 'docsSigned':
        return isVirtualCard ? EcommMachineEvents.APP_IN_PROGRESS : EcommMachineEvents.APP_FINISHED;
      case 'docsReady':
      case 'approved':
      case 'preApproved':
        return EcommMachineEvents.APP_IN_PROGRESS;
      case 'errorsFound':
        if (!isShortForm || ['creditCardNumber', 'creditCardExpiration', 'debitCardError', 'bankAccount'].includes(declineReason!)) {
          return EcommMachineEvents.APP_IN_PROGRESS;
        } else if (declineReason === 'address' || declineReason === 'ssn') {
          return EcommMachineEvents.CUSTOMER_LOOKUP_EVENT;
        }
        return EcommMachineEvents.ERROR;
      case 'draft':
        return EcommMachineEvents.APP_IN_PROGRESS;
      case 'declined':
        return EcommMachineEvents.DECLINE;
      default:
        return EcommMachineEvents.ERROR;
    }
  } else if (loanAppId && loanAppStatus) {
    switch (loanAppStatus) {
      case 'approved':
      case 'docs_ready':
      case 'accepted':
      case 'delivered':
        return EcommMachineEvents.APP_IN_PROGRESS;
      case 'errors_found':
      case 'draft':
        return EcommMachineEvents.CUSTOMER_LOOKUP_EVENT;
      case 'declined':
        return EcommMachineEvents.DECLINE;
      default:
        return EcommMachineEvents.ERROR;
    }
  }
  return EcommMachineEvents.CUSTOMER_LOOKUP_EVENT;
};
export default ecommMachine;
